import * as Yup from "yup";
import { areTimeframesValid } from "../components/UI/TimeframeSelector/timeframeHelpers";
import {
  Timeframe,
  VACANCY_CANDIDATE_VISIBILITY,
  VACANCY_START_AVAILABILITY,
} from "../models/vacancy";
import { getCandidateLogTimesheetSchema } from "../services/TimesheetService";

export const phoneRegExp = new RegExp(
  /^\+?((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/
);

const passwordRegex = new RegExp(
  /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/
);

const ALPHA_REGEX = new RegExp(
  // /^[a-zA-Z@]+$/
  /^(?!\s*$)(?!.*\d).*$/
);

const NUMBER_REGEX = new RegExp(/^[0-9]+$/);

const passwordError =
  "Password must be at least ten characters long and includes uppercase letters, lowercase letters, numbers, and special characters (!@#$%^&*).";

// Validation error messages constants
const VALIDATION_MESSAGES = {
  PHONE: {
    MIN_LENGTH: "Phone number must be atleast 10 characters",
    MAX_LENGTH: "Phone number must be at most 20 characters",
    INVALID_FORMAT: "Phone number is not valid",
    REQUIRED: "Phone Number is required",
    WORK_REQUIRED: "Work mobile number is required",
  },
  // Add other message categories as needed
  FIRST_NAME: {
    REQUIRED: "First Name is required",
    MAX_LENGTH: "First Name must be at most 50 characters",
    INVALID_FORMAT: "First name may only contain letters and special characters.",
  },
} as const;

export const characters =
  /^(?=.*[A-Za-z])[a-zA-Z0-9'\-_. ]+(?:\s[a-zA-Z0-9'_\-. ]+)*$/;
export const namePattern = /^[A-Za-z\s]+$/; // This is fine
export const pinFormat = /^(?:(?:[2-8]\d|9[0-7]|0?[28]|0?9(?=09))(?:\d{2}))$/; // This is fine
export const postCode = /^((0[289]\d{2})|([1-9]\d{3}))$/; // This is fine
export const customEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,3}$/i; // This is fine
export const format =
  /^(?:\+61)? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d{2}[- ]?\d{3}[- ]?\d{3})$/; // This is fine
export const posportVisa = /^[A-Z0-9]{8,13}$/; // This is fine
export const linkedinProfileUrl =
  /^((https?:\/\/)?(www\.)?linkedin\.com[/\w#?=-]+)$/; // This is fine
export const firstnameVar = / *\{[firstname]*\} */g;
export const lastnameVar = / *\{[lastname]*\} */g;
export const yearRegex = /^(19|20)\d{2}$/; // This is fine
export const numberRegex = /^[0-9\b]+$/; // This is fine
export const noSpcialChar = /^[a-zA-Z0-9 ,.]*$/; // This is fine

/**
 * Validates the start and end times.
 * Ensures that the start time is earlier than the end time
 * and the time difference is at least 2 hours.
 *
 * @param {string} start - The start time (e.g., "9 AM").
 * @param {string} end - The end time (e.g., "11 AM").
 * @returns {boolean} - Returns true if the time range is valid, otherwise false.
 */
const isValidTimeRange = (start: string, end: string): boolean => {
  const parseTime = (time: string): number => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes; // Convert time to total minutes
  };

  const startMinutes = parseTime(start);
  const endMinutes = parseTime(end);

  // Ensure start time is earlier than end time and at least 2 hours (120 minutes) apart
  return endMinutes > startMinutes && endMinutes - startMinutes >= 120;
};

const PhoneNumber = Yup.string()
  .min(10, VALIDATION_MESSAGES.PHONE.MIN_LENGTH)
  .max(20, VALIDATION_MESSAGES.PHONE.MAX_LENGTH)
  .matches(phoneRegExp, VALIDATION_MESSAGES.PHONE.INVALID_FORMAT)
  .required(VALIDATION_MESSAGES.PHONE.REQUIRED);

// Phone validation factory function for custom error messages
const createPhoneValidation = (requiredMessage?: string) => {
  return Yup.string()
    .min(10, VALIDATION_MESSAGES.PHONE.MIN_LENGTH)
    .max(20, VALIDATION_MESSAGES.PHONE.MAX_LENGTH)
    .matches(phoneRegExp, VALIDATION_MESSAGES.PHONE.INVALID_FORMAT)
    .required(requiredMessage || VALIDATION_MESSAGES.PHONE.REQUIRED);
};

const rules: any = {
  signup: {
    first_name: Yup.string()
      .required("First Name is required")
      .max(50, "First Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "First name may only contain letters and special characters."
      ),
    last_name: Yup.string()
      .required("Last Name is required")
      .max(50, "Last Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "Last name may only contain letters and special characters."
      ),
    email: Yup.string()
      .required("Email is required")
      .max(100, "Email must be at most 100 characters")
      .email("Email must be a valid email address"),
    address: Yup.string()
      .max(250, "Address must be at most 250 characters")
      .nullable(),
    password: Yup.string()
      .required("Password is required")
      .min(10, "Password length is minimum 10 characters")
      .max(20, "Password must be at most 20 characters")
      .matches(passwordRegex, passwordError),
    working_rights_in_australia: Yup.bool()
      .oneOf([true], "Australian Working Rights is mandatory")
      .required("Australian Working Rights is mandatory"),
  },
  signin: {
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required")
      .max(100, "Email must be at most 100 characters"),
    password: Yup.string().required("Password is required"),
  },
  forgotPassword: {
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Registered email is required")
      .max(100, "Email must be at most 100 characters"),
  },
  resetPassword: {
    email: Yup.string()
      .max(100, "Email must be at most 100 characters")
      .nullable(),
    token: Yup.string().nullable(),
    password: Yup.string()
      .min(10, "Password length is minimum 10 characters")
      .max(20, "Password must be at most 20 characters")
      .required("Password is required")
      .matches(passwordRegex, passwordError),
    confirm_password: Yup.string()
      .min(10, "Password length is minimum 10 characters")
      .max(20, "Password must be at most 20 characters")
      .oneOf([Yup.ref("password"), ""], "Passwords doesn't match")
      .required("Password confirmation is required"),
  },
  sentInvitation: {
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Registered email is required")
      .max(100, "Email must be at most 100 characters"),
  },
  personalDetails: {
    photo: Yup.string().required("Profile Picture is required"),
    first_name: Yup.string()
      .required("First Name is required")
      .max(50, "First Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "First name may only contain letters and special characters."
      ),
    last_name: Yup.string()
      .required("Last Name is required")
      .max(50, "Last Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "Last name may only contain letters and special characters."
      ),
    phone_number: Yup.string()
      .min(10, "Phone number must be atleast 10 characters")
      .max(20, "Phone number must be at most 20 characters")
      .matches(phoneRegExp, "Phone number is not valid")
      .required("Phone Number is required"),
    address: Yup.string()
      .max(250, "Address must be at most 250 characters")
      .required("Home Address is required"),
    resume: Yup.mixed().nullable(),
    working_rights: Yup.string().required("Working Rights is required"),
    permanent_employment: Yup.bool().nullable(),
  },
  qualifications: {
    qualification: Yup.string().required(
      "Early Childhood Education is required"
    ),
    studies_completed_location: Yup.string().when(
      "qualification",
      (qualification: string | string[], schema) => {
        if (Array.isArray(qualification)) {
          if (
            qualification[0] !== "currently_studying" &&
            qualification[0] !== "no_qualifications"
          ) {
            return schema.required("Studies Completed Location is required");
          }
        } else {
          if (
            qualification !== "currently_studying" &&
            qualification !== "no_qualifications"
          ) {
            return schema.required("Studies Completed Location is required");
          }
        }
        return schema.notRequired();
      }
    ),
    other_study_selected_country: Yup.string().when(
      "studies_completed_location",
      (studies_completed_location: string | string[], schema) => {
        if (Array.isArray(studies_completed_location)) {
          if (studies_completed_location[0] === "other") {
            return schema.required("Country is required");
          }
        } else {
          if (studies_completed_location[0] === "other") {
            return schema.required("Country is required");
          }
        }
        return schema.notRequired();
      }
    ),
    currently_studying_option: Yup.string().when(
      "qualification",
      (qualification: string | string[], schema) => {
        const qual = Array.isArray(qualification) ? qualification[0] : qualification;
        if (qual === "currently_studying") {
          return schema
            .required("Please select one of the currently studying options")
            .oneOf(
              ["diploma_equivalent", "certificate_iii_equivalent"],
              "Please select one of the currently studying options"
            );
        }
        return schema.notRequired();
      }
    ),
    acecqa_approved: Yup.boolean().nullable(),
    qualification_accept_options: Yup.array().min(0).nullable(),    
    alternative_positions: Yup.array().when(
      "qualification",
      (qualification: string | string[], schema) => {
        if (Array.isArray(qualification)) {
          if (
            qualification[0] === "currently_studying" ||
            qualification[0] === "no_qualifications"
          ) {
            return schema.required("Please select atleast one option").min(0);
          }
        } else {
          if (
            qualification[0] === "currently_studying" ||
            qualification[0] === "no_qualifications"
          ) {
            return schema.required("Please select atleast one option").min(0);
          }
        }
        return schema.notRequired();
      }
    ),
    wcc_valid_until: Yup.date().nullable(),
    first_aid_valid_until: Yup.date().nullable(),
    mandatory_reporting_valid_until: Yup.date().nullable(),
    teachers_registration_valid_until: Yup.date().nullable(),
    post_graduate_diploma_valid_until: Yup.date().nullable(),
    bachelor_or_degree_valid_until: Yup.date().nullable(),
    certificate_iii_valid_until: Yup.date().nullable(),
    food_handler_certificate_valid_until: Yup.date().nullable(),
    cooking_qualification_valid_until: Yup.date().nullable(),
    australian_work_visa_valid_until: Yup.date().nullable()

  },
  jobPreferences: {
    max_travel_distance: Yup.number().notRequired(),
    suburbs: Yup.array().nullable(), //min(4, "Please add minimum 5 suburbs"),
    preferred_transport_modes: Yup.array().nullable(), //min(0, "Please select atleast 1 mode of transport(s)"),
  },
  enquiry: {
    first_name: Yup.string()
      .required("First Name is required")
      .max(50, "First Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "First name may only contain letters and special characters."
      ),
    last_name: Yup.string()
      .required("Last Name is required")
      .max(50, "Last Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "Last name may only contain letters and special characters."
      ),
    email: Yup.string()
      .required("Email is required")
      .max(100, "Email must be at most 100 characters")
      .email("Email must be a valid email address"),
    phone_number: Yup.string()
      .min(10, "Phone number must be atleast 10 characters")
      .max(20, "Phone number must be at most 20 characters")
      .matches(phoneRegExp, "Phone number is not valid")
      .required("Phone number is required"),
    comment: Yup.string().nullable(),
    centre_information: Yup.array()
      .of(
        Yup.object().shape({
          business_name: Yup.string()
            .max(150, "Business name cannot exceed 150 characters")
            .required("Business Name is required"),
          website: Yup.string()
            .required("Website is required")
            .matches(
              /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
              "Website must be a valid URL"
            ),
          address: Yup.string()
            .max(250, "Address must be at most 250 characters")
            .required("Address is required"),
          center_phone: Yup.string()
            .min(10, "Phone number must be atleast 10 characters")
            .max(20, "Phone number must be at most 20 characters")
            .matches(phoneRegExp, "Phone number is not valid")
            .required("Phone number is required"),
        })
      )
      .required("Centre Information is required"),
    // .test(
    //   "unique-address-only",
    //   "Oops! It seems more than one centre has the same address. Please make sure each centre has a unique location.",
    //   (value = []) => {
    //     const seen = new Set();
    //     for (const item of value) {
    //       const addressKey = item.address?.trim().toLowerCase();
    //       if (seen.has(addressKey)) return false;
    //       seen.add(addressKey);
    //     }
    //     return true;
    //   }
    // ),
  },
  centreAdminEnquiry: {
    comment: Yup.string().nullable(),
    centre_information: Yup.array()
      .of(
        Yup.object().shape({
          business_name: Yup.string()
            .max(150, "Business name cannot exceed 150 characters")
            .required("Business Name is required"),
          website: Yup.string()
            .required("Website is required")
            .url("Website must be a valid URL"),
          address: Yup.string()
            .required("Address is required")
            .max(250, "Address must be at most 250 characters"),
          center_phone: Yup.string()
            .min(10, "Phone number must be atleast 10 characters")
            .max(20, "Phone number must be at most 20 characters")
            .matches(phoneRegExp, "Phone number is not valid")
            .required("Phone number is required"),
        })
      )
      .required("Centre Information is required"),
    // .test(
    //   "unique-address-only",
    //   "Oops! It seems more than one centre has the same address. Please make sure each centre has a unique location.",
    //   (value = []) => {
    //     const seen = new Set();
    //     for (const item of value) {
    //       const addressKey = item.address?.trim().toLowerCase();
    //       if (seen.has(addressKey)) return false;
    //       seen.add(addressKey);
    //     }
    //     return true;
    //   }
    // ),
  },
  adminUser: {
    role_id: Yup.number().required("User type is required"),
    first_name: Yup.string()
      .required("First Name is required")
      .max(50, "First Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "First name may only contain letters and special characters."
      ),
    last_name: Yup.string()
      .required("Last Name is required")
      .max(50, "Last Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "Last name may only contain letters and special characters."
      ),
    email: Yup.string()
      .required("Email is required")
      .max(100, "Email must be at most 100 characters")
      .email("Email must be a valid email address"),
    phone_number: Yup.string()
      .min(10, "Phone number must be atleast 10 characters")
      .max(20, "Phone number must be at most 20 characters")
      .matches(phoneRegExp, "Phone number is not valid")
      .required("Phone number is required"),

    /** 1  Access flag: required only for General Admin (role 2) */
    // has_all_centre_access: Yup.number().when("role_id", {
    //   is: (role: number | string) => String(role) === "4",
    //   then: (schema) => schema.oneOf([0, 1], "Choose Yes or No"),
    //   // .required("Required for centre user"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    /** 2  Centre list: required only when role = 2 AND has_all_centre_access = 0 */
    // centre_ids: Yup.array()
    // .of(Yup.number())
    // .when(["role_id", "has_all_centre_access"], {
    //   is: (role: number | string, access: number | undefined) =>
    //     String(role) === "2" && access === 0,
    //   then: (schema) => schema
    //     .required("At least one centre must be assigned")
    //     .min(1, "Please select atleast 1 centre"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),

    // /** 3 Timesheet access stays the same: required only for role 2 (optional)*/
    // timesheet_access: Yup.number().when("role_id", {
    //   is: (role: number | string) => String(role) === "2",
    //   then: (schema) =>
    //     schema
    //       .oneOf([0, 1], "Choose Yes or No"),
    //       // .required("Required for general admin"),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
  },
  availabilities: {
    availabilities: Yup.object()
      .shape({
        Everyday: Yup.array()
          .of(
            Yup.object().shape({
              start_time: Yup.string().required("Start time is required"),
              end_time: Yup.string()
                .required("End time is required")
                .test(
                  "is-valid-time-range",
                  "Time block can only be a minimum of 2 hours.",
                  function (value) {
                    const { start_time } = this.parent;
                    return isValidTimeRange(start_time, value || "");
                  }
                ),
            })
          )
          .min(1, "At least one time slot is required"),
        Monday: Yup.array().of(
          Yup.object().shape({
            start_time: Yup.string().required("Start time is required"),
            end_time: Yup.string()
              .required("End time is required")
              .test(
                "is-valid-time-range",
                "Time block can only be a minimum of 2 hours.",
                function (value) {
                  const { start_time } = this.parent;
                  return isValidTimeRange(start_time, value || "");
                }
              ),
          })
        ),
        Tuesday: Yup.array().of(
          Yup.object().shape({
            start_time: Yup.string().required("Start time is required"),
            end_time: Yup.string()
              .required("End time is required")
              .test(
                "is-valid-time-range",
                "Time block can only be a minimum of 2 hours.",
                function (value) {
                  const { start_time } = this.parent;
                  return isValidTimeRange(start_time, value || "");
                }
              ),
          })
        ),
        Wednesday: Yup.array().of(
          Yup.object().shape({
            start_time: Yup.string().required("Start time is required"),
            end_time: Yup.string()
              .required("End time is required")
              .test(
                "is-valid-time-range",
                "Time block can only be a minimum of 2 hours.",
                function (value) {
                  const { start_time } = this.parent;
                  return isValidTimeRange(start_time, value || "");
                }
              ),
          })
        ),
        Thursday: Yup.array().of(
          Yup.object().shape({
            start_time: Yup.string().required("Start time is required"),
            end_time: Yup.string()
              .required("End time is required")
              .test(
                "is-valid-time-range",
                "Time block can only be a minimum of 2 hours.",
                function (value) {
                  const { start_time } = this.parent;
                  return isValidTimeRange(start_time, value || "");
                }
              ),
          })
        ),
        Friday: Yup.array().of(
          Yup.object().shape({
            start_time: Yup.string().required("Start time is required"),
            end_time: Yup.string()
              .required("End time is required")
              .test(
                "is-valid-time-range",
                "Time block can only be a minimum of 2 hours.",
                function (value) {
                  const { start_time } = this.parent;
                  return isValidTimeRange(start_time, value || "");
                }
              ),
          })
        ),
        Saturday: Yup.array().of(
          Yup.object().shape({
            start_time: Yup.string().required("Start time is required"),
            end_time: Yup.string()
              .required("End time is required")
              .test(
                "is-valid-time-range",
                "Time block can only be a minimum of 2 hours.",
                function (value) {
                  const { start_time } = this.parent;
                  return isValidTimeRange(start_time, value || "");
                }
              ),
          })
        ),
        Sunday: Yup.array().of(
          Yup.object().shape({
            start_time: Yup.string().required("Start time is required"),
            end_time: Yup.string()
              .required("End time is required")
              .test(
                "is-valid-time-range",
                "Time block can only be a minimum of 2 hours.",
                function (value) {
                  const { start_time } = this.parent;
                  return isValidTimeRange(start_time, value || "");
                }
              ),
          })
        ),
      })
      .test(
        "at-least-one-selection",
        "At least one day must have a valid time slot",
        function (value) {
          if (!value) return false; // Ensure value exists
          return (Object.keys(value) as (keyof typeof value)[]).some(
            (day) =>
              Array.isArray(value?.[day] ?? []) &&
              (value?.[day]?.length ?? 0) > 0
          );
        }
      ),
  },
  nextOfKin: {
    first_name: Yup.string()
      .required("First Name is required")
      .max(50, "First Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "First name may only contain letters and special characters."
      ),
    last_name: Yup.string()
      .required("Last Name is required")
      .max(50, "Last Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "Last name may only contain letters and special characters."
      ),
    relationship: Yup.string()
      .required("Relationship is required")
      .max(50, "Relationship must be at most 50 characters"),
    phone_number: Yup.string()
      .min(10, "Phone number must be atleast 10 characters")
      .max(20, "Phone number must be at most 20 characters")
      .matches(phoneRegExp, "Phone number is not valid")
      .nullable(),
  },
  centerUsers: {
    first_name: Yup.string()
      .required("First Name is required")
      .max(50, "First Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "First name may only contain letters and special characters."
      ),
    last_name: Yup.string()
      .required("Last Name is required")
      .max(50, "Last Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "Last name may only contain letters and special characters."
      ),
    email: Yup.string()
      .required("Email is required")
      .max(100, "Email must be at most 100 characters")
      .matches(
        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        "Email must be a valid email address"
      ),
    role_id: Yup.number()
      .required("User type is required")
      .min(1, "User type is required"),
    phone_number: createPhoneValidation(VALIDATION_MESSAGES.PHONE.WORK_REQUIRED),
    /** 1  Access flag: required only for Centre User (role 4) */
    has_all_centre_access: Yup.number().when("role_id", {
      is: (role: number | string) => String(role) === "4",
      then: (schema) => schema.oneOf([0, 1], "Choose Yes or No"),
      // .required("Required for centre user"),
      otherwise: (schema) => schema.notRequired(),
    }),

    /** 2  Centre list: required only when role = 4 AND has_all_centre_access = 0 */
    centre_ids: Yup.array()
      .of(Yup.number())
      .when(["role_id", "has_all_centre_access"], {
        is: (role: number | string, access: number | undefined) =>
          String(role) === "4" && access === 0,
        then: (schema) =>
          schema
            .required("At least one centre must be assigned")
            .min(1, "Please select atleast one centre"),
        otherwise: (schema) => schema.notRequired(),
      }),
    timesheet_access: Yup.bool().nullable(),
  },
  adminCenterUsers: {
    first_name: Yup.string()
      .required("First Name is required")
      .max(50, "First Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "First name may only contain letters and special characters."
      ),
    last_name: Yup.string()
      .required("Last Name is required")
      .max(50, "Last Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "Last name may only contain letters and special characters."
      ),
    email: Yup.string()
      .required("Email is required")
      .max(100, "Email must be at most 100 characters")
      .matches(
        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        "Email must be a valid email address"
      ),
    role_id: Yup.number()
      .required("User type is required")
      .min(1, "User type is required"),
    phone_number: createPhoneValidation(VALIDATION_MESSAGES.PHONE.WORK_REQUIRED),
    centre_ids: Yup.array()
      .of(Yup.number())
      .min(1, "Please select atleast one centre"),
    timesheet_access: Yup.bool().nullable(),
  },
  centreDetails: {
    id: Yup.string().nullable(),
    photo: Yup.string().required(),
    business_name: Yup.string()
      .required("Business name is required")
      .max(150, "Business name cannot exceed 150 characters"),
    website: Yup.string()
      .required("Website is required") // Ensures the field is not empty
      .matches(
        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
        "Website must be a valid URL"
      ),
    phone: PhoneNumber,
    business_address: Yup.string()
      .required("Address is required")
      .max(250, "Address cannot exceed 250 characters"),
    franchise: Yup.string()
      .nullable()
      .max(150, "Franchise name cannot exceed 150 characters"),
    abn: Yup.string()
      .min(11, "ABN must be atleast 11 characters")
      .max(20, "ABN must be at most 20 characters")
      .matches(NUMBER_REGEX, "ABN is not valid")
      .required("ABN is required"),
    placements: Yup.string()
      // .nullable()
      .required("Licensed places is required")
      .matches(NUMBER_REGEX, "Licensed places is not valid")
      .max(3, "Licensed places cannot exceed 3 digits"),
    first_name: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? Yup.string()
            .required("First Name is required")
            .max(50, "First Name must be at most 50 characters")
            .matches(
              ALPHA_REGEX,
              "First name may only contain letters and special characters."
            )
        : Yup.string().max(50, "First Name must be at most 50 characters");
    }),
    last_name: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? Yup.string()
            .required("Last Name is required")
            .max(50, "Last Name must be at most 50 characters")
            .matches(
              ALPHA_REGEX,
              "Last name may only contain letters and special characters."
            )
        : Yup.string().max(50, "Last Name must be at most 50 characters");
    }),
    email: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? Yup.string()
            .required("Email is required")
            .max(100, "Email must be at most 100 characters")
            .email("Email must be a valid email address")
        : Yup.string().max(100, "Email must be at most 100 characters");
    }),
    user_phone: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? PhoneNumber
        : Yup.string().max(20, "Phone Number must be at most 20 characters");
    }),
    profile: Yup.string()
      .trim() // Removes leading and trailing spaces
      .max(1000, "Centre profile cannot exceed 1000 characters")
      .nullable(),
    expectations: Yup.string()
      .trim() // Removes leading and trailing spaces
      .max(1000, "Centre expectations cannot exceed 1000 characters")
      .nullable(),
    transport: Yup.array().nullable(),
    timings: Yup.mixed().nullable(),
    centre_invoice_email: Yup.lazy((value, context) => {
      return Yup.string()
        .required("Email is required")
        .max(100, "Email must be at most 100 characters")
        .email("Email must be a valid email address");
    }),
  },
  adminCentreDetails: {
    id: Yup.string().nullable(),
    photo: Yup.string().nullable(),
    business_name: Yup.string()
      .required("Business name is required")
      .max(150, "Business name cannot exceed 150 characters"),
    website: Yup.string()
      .required("Website is required") // Ensures the field is not empty
      .matches(
        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
        "Website must be a valid URL"
      ),
    phone: PhoneNumber,
    business_address: Yup.string()
      .required("Address is required")
      .max(250, "Address cannot exceed 250 characters"),
    franchise: Yup.string()
      .nullable()
      .max(150, "Franchise name cannot exceed 150 characters"),
    abn: Yup.string()
      .min(11, "ABN must be atleast 11 characters")
      .max(20, "ABN must be at most 20 characters")
      .matches(NUMBER_REGEX, "ABN is not valid")
      .nullable(),
    placements: Yup.string()
      .nullable()
      // .required("Licensed places is required")
      .matches(NUMBER_REGEX, "Licensed places is not valid")
      .max(3, "Licensed places cannot exceed 3 digits"),
    first_name: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? Yup.string()
            .required("First Name is required")
            .max(50, "First Name must be at most 50 characters")
            .matches(
              ALPHA_REGEX,
              "First name may only contain letters and special characters."
            )
        : Yup.string().max(50, "First Name must be at most 50 characters");
    }),
    last_name: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? Yup.string()
            .required("Last Name is required")
            .max(50, "Last Name must be at most 50 characters")
            .matches(
              ALPHA_REGEX,
              "Last name may only contain letters and special characters."
            )
        : Yup.string().max(50, "Last Name must be at most 50 characters");
    }),
    email: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? Yup.string()
            .required("Email is required")
            .max(100, "Email must be at most 100 characters")
            .email("Email must be a valid email address")
        : Yup.string().max(100, "Email must be at most 100 characters");
    }),
    user_phone: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? PhoneNumber
        : Yup.string().max(20, "Phone Number must be at most 20 characters");
    }),
    profile: Yup.string()
      .trim() // Removes leading and trailing spaces
      .max(1000, "Centre profile cannot exceed 1000 characters")
      .nullable(),
    expectations: Yup.string()
      .trim() // Removes leading and trailing spaces
      .max(1000, "Centre expectations cannot exceed 1000 characters")
      .nullable(),
    transport: Yup.array().nullable(),
    timings: Yup.mixed().nullable(),
    centre_invoice_email: Yup.lazy((value, context) => {
      return context.parent.id === "0"
        ? Yup.string()
            .required("Email is required")
            .max(100, "Email must be at most 100 characters")
            .email("Email must be a valid email address")
        : Yup.string().max(100, "Email must be at most 100 characters");
    })
  },
  activate_profile_super_admin: {
    action: Yup.string().required("Action is required"),
    bypass_id_verification: Yup.number().nullable(),
    bypass_online_interview: Yup.number().nullable(),
    bypass_compliance_check: Yup.number().nullable(),
    id_verification_bypass_reason: Yup.string().when(
      "bypass_id_verification",
      (value: number | string[], schema) => {
        if (value === 1) {
          return schema.required("Reason is required");
        }
        return schema.notRequired();
      }
    ),
    online_interview_bypass_reason: Yup.string().when(
      "bypass_online_interview",
      (value: number | string[], schema) => {
        if (value === 1) {
          return schema.required("Reason is required");
        }
        return schema.notRequired();
      }
    ),
    complaince_check_bypass_reason: Yup.string().when(
      "bypass_compliance_check",
      (value: number | string[], schema) => {
        if (value === 1) {
          return schema.required("Reason is required");
        }
        return schema.notRequired();
      }
    ),
  },
  admincomments: {
    comments: Yup.string()
      .required("Comments are required")
      .max(500, "Comments cannot exceed 500 characters"),
  },
  edit_completed_personal_details: {
    address: Yup.string()
      .required("Home Address is required")
      .max(250, "Home Address must be at most 250 characters"),
    resume: Yup.mixed().required("Resume is required"),
    resume_name: Yup.string().required("Resume name is required"),
    permanent_employment: Yup.bool().nullable(),
    phone_number: PhoneNumber,
  },
  edit_completed_qualifications: {
    qualification_accept_options: Yup.array().min(0).nullable(),
    diploma_jobs_at_certificate_level: Yup.string().when(
      "qualification",
      (qualification: string | string[], schema) => {
        if (Array.isArray(qualification)) {
          if (qualification[0] === "diploma") {
            return schema.required("Please select atleast one option");
          }
        } else {
          if (qualification === "diploma") {
            return schema.required("Please select atleast one option");
          }
        }
        return schema.notRequired();
      }
    ),
    alternative_positions: Yup.array().when(
      "qualification",
      (qualification: string | string[], schema) => {
        if (Array.isArray(qualification)) {
          if (           
            qualification[0] === "no_qualifications"
          ) {
            return schema
              .required("Please select atleast one option")
              .min(1, "Please select at least one option");
          }
        } else {
          if (           
            qualification[0] === "no_qualifications"
          ) {
            return schema
              .required("Please select atleast one option")
              .min(1, "Please select at least one option");
          }
        }
        return schema.notRequired();
      }
    ),
  },
  profile_make_a_request: {
    details: Yup.string()
      .required("Comments are required")
      .max(500, "Comments cannot exceed 500 characters"),
    request_id: Yup.string().required("please upload at least one document"),
  },
  setUpApprovedCentres: {
    business_details: Yup.array().of(
      Yup.object().shape({
        photo: Yup.string().nullable(),
        business_name: Yup.string()
          .required("Business name is required")
          .max(150, "Business name cannot exceed 150 characters"),
        website: Yup.string()
          .required("Website is required") // Ensures the field is not empty
          .matches(
            /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
            "Website must be a valid URL"
          ),
        phone: PhoneNumber,
        address: Yup.string()
          .required("Address is required")
          .max(250, "Address cannot exceed 250 characters"),
        abn: Yup.string()
          .min(11, "ABN must be atleast 11 characters")
          .max(20, "ABN must be at most 20 characters")
          .matches(NUMBER_REGEX, "ABN is not valid")
          // .required("ABN is required"),
          .nullable(),
        placements: Yup.string()
          .nullable()
          .matches(NUMBER_REGEX, "Licensed places is not valid")
          .max(3, "Licensed places cannot exceed 3 digits"),
        first_name: Yup.string()
          .required("First Name is required")
          .max(50, "First Name must be at most 50 characters")
          .matches(
            ALPHA_REGEX,
            "First name may only contain letters and special characters."
          ),
        last_name: Yup.string()
          .required("Last Name is required")
          .max(50, "Last Name must be at most 50 characters")
          .matches(
            ALPHA_REGEX,
            "Last name may only contain letters and special characters."
          ),
        email: Yup.string()
          .required("Email is required")
          .max(100, "Email must be at most 100 characters")
          .email("Email must be a valid email address"),
        user_phone: Yup.string()
          .min(10, "Phone number must be atleast 10 characters")
          .max(20, "Phone number must be at most 20 characters")
          .matches(phoneRegExp, "Phone number is not valid")
          .required("Phone number is required"),
        profile: Yup.string()
          .trim() // Removes leading and trailing spaces
          .nullable()
          .max(1000, "Centre profile cannot exceed 1000 characters"),
        expectations: Yup.string()
          .trim() // Removes leading and trailing spaces
          .nullable()
          .max(1000, "Centre expectations cannot exceed 1000 characters"),
        timings: Yup.mixed().nullable(),
        transport: Yup.array().nullable(),
        centre_invoice_email: Yup.string()
          .required("Email is required")
          .max(100, "Email must be at most 100 characters")
          .email("Email must be a valid email address"),
      })
    ),
  },
  vacancy: {
    centre_id: Yup.number().required("Please select centre"),
    timeline_type: Yup.string().required("Timeline type is required"),
    // Timeframes must be an array of objects, each containing start_time, end_time, and a date range
    timeframes: Yup.array()
      .of(
        Yup.object().shape({
          // date must be an array of two nullable Dates
          date: Yup.array()
            .of(Yup.date().nullable())
            .required("Please enter a valid timeframe")
            // Require exactly two dates (start and end)
            .length(2, "Both start and end date must be selected")
            // Custom validation: both start and end must be truthy
            .test(
              "valid-date-pair",
              "Invalid date range",
              (date) => !!(date?.[0] && date?.[1])
            ),
          start_time: Yup.string().required("Please enter a valid timeframe"),
          end_time: Yup.string().required("Please enter a valid timeframe"),
        })
      )
      // Custom test: ensure timeframes do not overlap and follow rules based on timeline_type
      .test(
        "no-overlap",
        "Please enter a valid timeframes", // default fallback message
        function (value) {
          const { timeline_type, isEdit, created_at } = this.parent;

          const result = areTimeframesValid(
            value as Timeframe[],
            timeline_type as VACANCY_START_AVAILABILITY,
            isEdit as boolean,
            created_at as string
          );

          if (!result.valid && typeof result.index === "number") {
            return this.createError({
              path: `timeframes[${result.index}]`,
              message: result.error ?? "Timeframes are invalid",
            });
          }

          return true;
        }
      ),
    position_uuids: Yup.array()
      .of(Yup.string())
      .test(
        "position-or-alternate",
        "Please select candidate levels or an alternate position, not both",
        function (value) {
          const { alternate_position_legacy } = this.parent;
          const hasPosition = value && value.length > 0;
          const hasAlternate = !!alternate_position_legacy;

          if (hasPosition && hasAlternate) return false;
          return true;
        }
      )
      .test(
        "position-required-if-no-alternate",
        "Please select candidate levels or an alternate position",
        function (value) {
          const { alternate_position_legacy } = this.parent;
          const hasPosition = value && value.length > 0;
          const hasAlternate = !!alternate_position_legacy;

          if (!hasPosition && !hasAlternate) return false;
          return true;
        }
      ),

    alternate_position_legacy: Yup.string()
      .nullable()
      .test(
        "alternate-required-if-no-position",
        "Please select candidate levels or an alternate position",
        function (value) {
          const { position_uuids } = this.parent;
          const hasPosition = position_uuids && position_uuids.length > 0;
          const hasAlternate = !!value;

          if (!hasPosition && !hasAlternate) return false;
          return true;
        }
      )
      .test(
        "alternate-invalid-if-position-present",
        "You can't select both candidate levels and an alternate position",
        function (value) {
          const { position_uuids } = this.parent;
          const hasPosition = position_uuids && position_uuids.length > 0;
          const hasAlternate = !!value;

          if (hasPosition && hasAlternate) return false;
          return true;
        }
      ),
    visibility_type: Yup.string().required(
      "Please specify who can view and accept the vacancy"
    ),
    // preferred_candidate_uuids: Yup.array()
    //   .of(Yup.string().uuid())
    //   .when("visibility_type", {
    //     is: VACANCY_CANDIDATE_VISIBILITY.PREFERRED_CANDIDATES,
    //     then: (schema) => schema.min(1, "Please select a candidate"),
    //     otherwise: (schema) => schema.notRequired(),
    //   }),
    selected_candidate_id: Yup.number()
      .nullable()
      .test(
        "selected-candidate-conditional-validation",
        "", // dynamic message injected through createError
        function (value) {
          const {
            visibility_type,
            centre_id,
            timeframes,
            timeline_type,
            isEdit,
            created_at,
          } = this.parent;

          // 1. If not SELECTED_CANDIDATE, skip validation
          if (
            visibility_type !== VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE
          ) {
            return true;
          }

          // 2. If no centre_id
          if (!centre_id) {
            return this.createError({
              message: "Please select centre before selecting a candidate",
            });
          }

          // 3. If no timeline_type
          if (!timeline_type) {
            return this.createError({
              message:
                "Please select timeline type before selecting a candidate",
            });
          }

          // 4. If timeframes are invalid (only after timeline_type is selected)
          const hasValidTimeframes = timeframes.every(
            (tf: any) =>
              tf.date?.[0] && tf.date?.[1] && tf.start_time && tf.end_time
          );
          const result = areTimeframesValid(
            timeframes,
            timeline_type,
            isEdit,
            created_at
          );

          if (!hasValidTimeframes || !result.valid) {
            return this.createError({
              message:
                result.error ||
                "Timeframes must be valid before selecting a candidate",
            });
          }

          // 5. If candidate not selected
          if (value === null || value === undefined) {
            return this.createError({
              message: "Please select a candidate",
            });
          }

          return true;
        }
      ),
    response_timeframe_check: Yup.boolean().nullable(),
    response_timeframe: Yup.number().when(
      ["visibility_type", "response_timeframe_check"],
      {
        is: (visibility: string, checked: boolean | null) =>
          checked === true &&
          (visibility === VACANCY_CANDIDATE_VISIBILITY.PREFERRED_CANDIDATES ||
            visibility === VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE),
        then: (schema) =>
          schema
            .required("Please select a valid timeframe")
            .moreThan(0, "Please select a valid timeframe"),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    all_response_timeframe_check: Yup.boolean().nullable(),
    all_response_timeframe: Yup.number().when(
      ["visibility_type", "all_response_timeframe_check"],
      {
        is: (visibility: string, checked: boolean) =>
          checked === true &&
          visibility === VACANCY_CANDIDATE_VISIBILITY.SELECTED_CANDIDATE,
        then: (schema) =>
          schema
            .required("Please select a valid timeframe")
            .test(
              "greater-than-preferred",
              "Must be longer than the preferred candidates timeframe",
              function (value) {
                const { response_timeframe_check, response_timeframe } =
                  this.parent;
                if (!response_timeframe_check) return true;
                if (!value || !response_timeframe) return true;
                return value > response_timeframe;
              }
            ),
        otherwise: (schema) => schema.notRequired(),
      }
    ),

    // allowance: Yup.boolean().required(
    //   "Please select if a daily allowance is needed"
    // ),
    // allowance_amount: Yup.number()
    //   .nullable()
    //   .when("allowance", {
    //     is: true,
    //     then: (schema) =>
    //       schema
    //         .required("Please select an allowance amount")
    //         .oneOf([25, 50], "Invalid allowance amount"),
    //     otherwise: (schema) => schema.nullable().notRequired(),
    //   }),
  },
  withdraw_job_application: {
    comments: Yup.string()
      .required("Comments is required")
      .max(1000, "Centre profile cannot exceed 1000 characters"),
  },
  bankAccount: {
    account_name: Yup.string()
      .required("Account Name is required")
      .max(50, "Account Name must be at most 50 characters")
      .matches(
        ALPHA_REGEX,
        "Account name may only contain letters and special characters."
      ),
    account_bsb: Yup.string()
      .length(6, "BSB must be exactly 6 digits")
      .matches(/^\d{6}$/, "BSB must be a valid 6-digit number")
      .required("BSB is required"),
    account_number: Yup.string()
      .min(6, "Account number must be at least 6 digits")
      .max(10, "Account number must be at most 10 digits")
      .matches(/^\d+$/, "Account number must contain only digits")
      .required("Account Number is required"),
  },
  approve_timesheet: {
    loading_base_rate: Yup.number()
      .nullable()
      .when("did_not_work", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema
            .typeError("Base Rate must be a number")
            .required("Base Rate is required")
            .moreThan(0, "Base Rate must be greater than 0"),
      }),
    loading_rate: Yup.number()
      .nullable()
      .when("did_not_work", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema.when([], {
            is: (val: any) => val !== null && val !== undefined && val !== "",
            then: (schema) =>
              schema
                .typeError("Loading rate must be a number")
                .moreThan(0, "Loading rate must be greater than 0"),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    loading_charge_rate: Yup.number()
      .nullable()
      .when("did_not_work", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema
            .typeError("Charge Rate must be a number")
            .required("Charge Rate is required")
            .moreThan(0, "Charge Rate must be greater than 0"),
      }),
    //   allowance_base_rate: Yup.number()
    //     .nullable()
    //     .notRequired()
    //     .when([], {
    //       is: (val: any) => val !== null && val !== undefined && val !== "",
    //       then: (schema) => schema
    //         .typeError("Allowance Amount must be a number")
    //         .moreThan(0, "Allowance Amount must be greater than 0"),
    //       otherwise: (schema) => schema.notRequired(),
    //     }),
    //   // allowance_charge_rate: Yup.number()
    //   //   .typeError("Allowance Charge must be a number")
    //   //   .required("Allowance Charge is required")
    //   //   .moreThan(0, "Allowance Charge must be greater than 0"),
  },
  profile_document_renew: {
    attachment: Yup.mixed()
      .required("Please upload a document")
      .test(
        "fileExists",
        "Please upload a document",
        (value) => value !== null && value !== undefined && value !== ""
      ),
    document_date: Yup.date()
      .required("Please select a valid date")
      .typeError("Please select a valid date"),
  },
  casualAndFairWorkStatement: {
    accept_terms: Yup.boolean().required("Please accept the terms to continue."),
  }
};

export const createValidationSchema = (
  entity: string,
  actualJobStartTime?: string,
  actualJobEndTime?: string,
  minBaseRate?: string,
  minChargeRate?: string,
  did_not_work?: boolean
) => {
  let fields: any = rules[entity];
  if (
    entity === "candidate_log_timesheet" &&
    actualJobStartTime &&
    actualJobEndTime
  ) {
    fields = getCandidateLogTimesheetSchema(
      actualJobStartTime,
      actualJobEndTime,
      did_not_work
    );
  }

  if (
    entity === "approve_timesheet" &&
    minBaseRate !== "" &&
    minChargeRate !== ""
  ) {
    fields.loading_base_rate = Yup.number()
      .nullable()
      .when("did_not_work", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema
            .typeError("Base Rate must be a number")
            .required("Base Rate is required")
            .moreThan(0, "Base Rate must be greater than 0")
            .min(Number(minBaseRate), `Base Rate must be at least $${minBaseRate}`),
      });

    fields.loading_charge_rate = Yup.number()
      .nullable()
      .when("did_not_work", {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema
            .typeError("Charge Rate must be a number")
            .required("Charge Rate is required")
            .moreThan(0, "Charge Rate must be greater than 0")
            .min(
              Number(minChargeRate),
              `Charge Rate must be at least $${minChargeRate}`
            ),
      });
  }
  return Yup.object().shape(fields);
};
